import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../services/firebase';
import { validatePassword } from '../utils/passwordValidator';
import PasswordRequirements from './PasswordRequirements';
import { t } from '../i18n';
import './EmailActionHandler.css';

const logo = `${process.env.PUBLIC_URL}/logo.svg`;

const EmailActionHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<string | null>(null);
  const [actionCode, setActionCode] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Password validation
  const passwordValidation = validatePassword(newPassword);

  useEffect(() => {
    const modeParam = searchParams.get('mode');
    const codeParam = searchParams.get('oobCode');

    setMode(modeParam);
    setActionCode(codeParam);

    if (modeParam === 'verifyEmail' && codeParam) {
      handleVerifyEmail(codeParam);
    } else if (modeParam === 'resetPassword' && codeParam) {
      handleResetPassword(codeParam);
    } else if ((modeParam === 'verifyAndChangeEmail' || modeParam === 'recoverEmail') && codeParam) {
      handleApplyCode(codeParam, modeParam);
    } else {
      setStatus('error');
      setMessage(t('emailAction.invalidRequest'));
    }
  }, [searchParams]);

  const handleVerifyEmail = async (code: string) => {
    try {
      await applyActionCode(auth, code);
      setStatus('success');
      setMessage(t('emailAction.emailVerified'));
    } catch (error: any) {
      console.error('Error verifying email:', error);
      setStatus('error');
      if (error.code === 'auth/invalid-action-code') {
        setMessage(t('emailAction.verificationExpired'));
      } else {
        setMessage(t('emailAction.verifyFailed'));
      }
    }
  };

  // Email-change confirmation / email recovery — both applied with applyActionCode.
  const handleApplyCode = async (code: string, m: string) => {
    try {
      await applyActionCode(auth, code);
      setStatus('success');
      setMessage(m === 'recoverEmail' ? t('emailAction.emailRecovered') : t('emailAction.emailChanged'));
    } catch (error: any) {
      console.error('Error applying action code:', error);
      setStatus('error');
      setMessage(t('emailAction.emailChangeFailed'));
    }
  };

  const handleResetPassword = async (code: string) => {
    try {
      await verifyPasswordResetCode(auth, code);
      setStatus('success');
      setMessage(t('emailAction.enterNewPassword'));
      setIsResettingPassword(true);
    } catch (error: any) {
      console.error('Error verifying reset code:', error);
      setStatus('error');
      if (error.code === 'auth/invalid-action-code') {
        setMessage(t('emailAction.resetExpired'));
      } else {
        setMessage(t('emailAction.invalidResetLink'));
      }
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordValidation.isValid) {
      setMessage(t('password.ensureRequirements'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage(t('emailAction.passwordsDoNotMatch'));
      return;
    }

    if (!actionCode) return;

    try {
      setStatus('loading');
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
      setMessage(t('emailAction.passwordResetSuccess'));
      setIsResettingPassword(false);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setStatus('error');
      setMessage(t('emailAction.resetFailed'));
    }
  };

  const handleContinue = () => {
    const continueUrl = searchParams.get('continueUrl');
    if (continueUrl) {
      window.location.href = continueUrl;
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="email-action-container">
      <div className="email-action-background" />

      <div className="email-action-content">
        <div className="email-action-header">
          <img src={logo} alt="Urunn" className="email-action-logo" />
        </div>

        <div className="email-action-card">
          {status === 'loading' && (
            <>
              <h2 className="email-action-title">{t('emailAction.processing')}</h2>
              <p className="email-action-message">{t('emailAction.pleaseWait')}</p>
            </>
          )}

          {status === 'success' && !isResettingPassword && (
            <>
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="30" stroke="#E4FF78" strokeWidth="4"/>
                  <path d="M20 32L28 40L44 24" stroke="#E4FF78" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="email-action-title">{message}</h2>
              {mode === 'verifyEmail' && (
                <p className="email-action-message">{t('emailAction.signInMessage')}</p>
              )}
              <button onClick={handleContinue} className="email-action-button">
                {t('common.continue')}
              </button>
            </>
          )}

          {status === 'success' && isResettingPassword && (
            <>
              <h2 className="email-action-title">{t('emailAction.resetPasswordTitle')}</h2>
              <p className="email-action-message">{t('emailAction.enterNewPasswordBelow')}</p>

              <form onSubmit={handlePasswordReset} className="password-reset-form">
                <div className="input-field">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder={t('emailAction.newPasswordPlaceholder')}
                    required
                  />
                </div>

                <PasswordRequirements
                  validation={passwordValidation}
                  show={passwordFocused || (newPassword.length > 0 && !passwordValidation.isValid)}
                />

                <div className="input-field">
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('emailAction.confirmPasswordPlaceholder')}
                    required
                  />
                </div>

                <button type="submit" disabled={newPassword.length > 0 && !passwordValidation.isValid} className="email-action-button">
                  {t('emailAction.resetPasswordButton')}
                </button>
              </form>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="error-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="30" stroke="#FF4444" strokeWidth="4"/>
                  <path d="M32 20V36M32 44H32.02" stroke="#FF4444" strokeWidth="4" strokeLinecap="round"/>
                </svg>
              </div>
              <h2 className="email-action-title">{t('emailAction.errorTitle')}</h2>
              <p className="email-action-message">{message}</p>
              <button onClick={() => navigate('/login')} className="email-action-button-secondary">
                {t('emailAction.goToLogin')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailActionHandler;
